/**
 * Injects tracking scripts into the document head sequentially
 * Works in both Next.js and Vite environments
 * Handles document.write calls similar to postscribe
 * @param {string} trackingCodes - HTML string containing script tags
 * @returns {Promise<void>}
 */
export const injectTrackingScript = trackingCodes => {
  return new Promise((resolve, reject) => {
    // Only run in browser environment
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    // define here so we can restore them in the catch block if needed
    let originalWrite;
    let originalWriteln;

    try {
      // Create a temporary div to parse HTML string
      const div = document.createElement('div');
      div.innerHTML = trackingCodes;

      // Find all script tags
      const scripts = Array.from(div.getElementsByTagName('script'));

      // If no scripts found, resolve immediately
      if (scripts.length === 0) {
        resolve();
        return;
      }

      // Create a container for document.write output
      const writeContainer = document.createElement('div');
      document.body.appendChild(writeContainer);

      // Store original document.write
      originalWrite = document.write;
      originalWriteln = document.writeln;

      // Override document.write to append to our container
      document.write = document.writeln = html => {
        writeContainer.innerHTML += html;
      };

      // Load scripts sequentially
      const loadScriptSequentially = index => {
        if (index >= scripts.length) {
          // Restore original document.write
          document.write = originalWrite;
          document.writeln = originalWriteln;
          resolve();
          return;
        }

        const script = scripts[index];
        const newScript = document.createElement('script');

        // Copy all attributes
        Array.from(script.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });

        // Handle script loading
        const handleScriptComplete = () => {
          // Process any document.write output
          if (writeContainer.innerHTML) {
            const writtenContent = writeContainer.innerHTML;
            writeContainer.innerHTML = '';

            // Handle any scripts in the written content
            const writtenDiv = document.createElement('div');
            writtenDiv.innerHTML = writtenContent;
            const writtenScripts = Array.from(writtenDiv.getElementsByTagName('script'));

            // Insert non-script content
            writtenDiv.querySelectorAll(':not(script)').forEach(el => {
              writeContainer.appendChild(el.cloneNode(true));
            });

            // Add written scripts to our queue
            scripts.splice(index + 1, 0, ...writtenScripts);
          }

          // Load next script
          loadScriptSequentially(index + 1);
        };

        newScript.onload = handleScriptComplete;
        newScript.onerror = error => {
          console.warn('Script loading error:', error);
          handleScriptComplete(); // Continue with next script
        };

        // Handle inline scripts
        if (script.innerHTML) {
          newScript.innerHTML = script.innerHTML;
          document.head.appendChild(newScript);
          // For inline scripts, trigger complete immediately
          setTimeout(handleScriptComplete, 0);
        } else {
          // For external scripts, wait for load
          document.head.appendChild(newScript);
        }
      };

      // Start loading the first script
      loadScriptSequentially(0);
    } catch (error) {
      // Restore original document.write in case of error
      if (typeof document !== 'undefined') {
        document.write = originalWrite;
        document.writeln = originalWriteln;
      }
      console.error('Error injecting tracking scripts:', error);
      reject(error);
    }
  });
};
